<template>
    <div class="playback-page">
        <div class="playback-header">
            <div class="ph-brand">
                <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                    <rect x="2" y="6" width="13" height="12" rx="3" fill="#2D8CFF"/>
                    <path d="M16 10.5 L21 7.5 v9 L16 13.5 z" fill="#2D8CFF"/>
                </svg>
                <span>腾讯会议</span>
            </div>
            <el-button type="text" icon="el-icon-back" @click="handleBack">返回</el-button>
            <span class="playback-title">{{ record.topic || '录像回放' }}</span>
            <span class="playback-meta">{{ record.meeting_time }}</span>
            <span class="playback-meta" v-if="record.meeting_code">
                会议号:{{ formatCode(record.meeting_code) }}
            </span>
            <span class="ph-user" v-if="user && user.name">
                <i class="el-icon-user"></i> {{ user.name }}
            </span>
        </div>

        <!-- 分享链接设了观看密码 -->
        <div class="playback-password" v-if="need_password">
            <div class="pp-card">
                <div class="pp-title"><i class="el-icon-lock"></i> 该回放需要输入密码</div>
                <el-input v-model="share_password" placeholder="请输入观看密码" maxlength="32"
                          show-password @keyup.enter.native="submitPassword" autofocus></el-input>
                <div class="pp-error" v-if="password_error">{{ password_error }}</div>
                <el-button type="primary" style="width:100%;margin-top:12px" :loading="loading"
                           @click="submitPassword">观看
                </el-button>
            </div>
        </div>

        <div class="playback-error" v-else-if="error">
            <el-alert :title="error" type="error" :closable="false" show-icon></el-alert>
        </div>

        <div class="playback-body" v-else v-loading="loading">
            <div class="playback-left">
                <div class="playback-video-wrap">
                    <video ref="video" controls :src="video_url" v-if="video_url"
                           @timeupdate="handleTimeUpdate" @error="video_error = true"
                           @loadedmetadata="handleMeta" @durationchange="handleMeta"></video>
                    <div class="playback-video-empty" v-else-if="!loading">
                        未获取到播放地址,录像可能仍在生成
                    </div>
                    <div class="playback-video-expired" v-if="video_error">
                        <span>播放地址可能已过期</span>
                        <el-button size="mini" type="primary" @click="load(true)">刷新地址</el-button>
                    </div>
                </div>

                <div class="playback-track" v-if="duration_ms && chapters.length"
                     ref="track" @click="handleTrackClick" title="点击跳转">
                    <div class="track-progress" :style="{width: progressPct + '%'}"></div>
                    <div class="track-dot" v-for="(c, i) in chapters" :key="i"
                         :style="{left: dotPct(c) + '%'}"
                         :class="{active: currentChapterIndex === i}"
                         :title="formatMs(c.start_time) + ' ' + c.name"
                         @click.stop="seekTo(c.start_time)"></div>
                </div>

                <div class="playback-summary" v-if="summaryText">
                    <div :class="{'summary-collapsed': !summary_expanded}">{{ summaryText }}</div>
                    <el-button type="text" size="mini" @click="summary_expanded = !summary_expanded">
                        {{ summary_expanded ? '收起' : '展开更多' }}
                    </el-button>
                </div>

                <div class="playback-chapters" v-if="chapters.length" ref="chapters">
                    <div class="chapter-item" v-for="(c, i) in chapters" :key="i"
                         :class="{active: currentChapterIndex === i}"
                         @click="seekTo(c.start_time)">
                        <img :src="c.pic_url" v-if="c.pic_url" loading="lazy" alt=""/>
                        <div class="chapter-time">{{ formatMs(c.start_time) }}</div>
                        <div class="chapter-name">{{ c.name }}</div>
                    </div>
                </div>
            </div>

            <div class="playback-divider" @mousedown.prevent="startResize"></div>

            <div class="playback-right" :style="{width: right_width + 'px'}">
                <el-tabs v-model="tab">
                    <el-tab-pane label="转写" name="transcript">
                        <playback-transcript
                            :transcript="transcript"
                            :keywords="keywords"
                            :current-ms="current_ms"
                            @seek="seekTo"
                        ></playback-transcript>
                    </el-tab-pane>
                    <el-tab-pane label="纪要" name="minutes">
                        <div class="pane-scroll">
                            <template v-if="minutes && (minutes.minute || minutes.todo)">
                                <div class="minutes-html" v-html="renderedMinutes"></div>
                                <template v-if="minutes.todo">
                                    <h4 class="mt-3">会议待办</h4>
                                    <div class="minutes-html" v-html="renderLightMd(minutes.todo)"></div>
                                </template>
                            </template>
                            <div class="pane-empty" v-else>该录像未生成智能纪要(内容较短或仍在生成)</div>
                        </div>
                    </el-tab-pane>
                    <el-tab-pane label="话题" name="topics" v-if="topics && topics.length">
                        <div class="pane-scroll">
                            <div class="topic-item" v-for="(t, i) in topics" :key="i">
                                <div class="topic-title">{{ t.name }}</div>
                                <div class="topic-segs" v-if="t.segments && t.segments.length">
                                    <el-tag v-for="(g, gi) in t.segments" :key="gi" size="mini"
                                            effect="plain" class="topic-seg"
                                            @click="seekTo(g.start_time)">{{ formatMs(g.start_time) }}</el-tag>
                                </div>
                            </div>
                        </div>
                    </el-tab-pane>
                </el-tabs>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapState} from 'vuex';
    import axios from 'axios';
    import PlaybackTranscript from '../components/playback-transcript';

    export default {
        name: 'record-playback',
        components: {PlaybackTranscript},
        data() {
            return {
                loading: true,
                error: '',
                need_password: false,
                share_password: '',
                password_error: '',
                record: {},
                video_url: '',
                video_error: false,
                summary: '',
                summary_expanded: false,
                keywords: [],
                chapters: [],
                transcript: null,
                minutes: null,
                topics: [],
                tab: 'transcript',
                current_ms: 0,
                duration_ms: 0,
                right_width: 420,
            };
        },
        computed: {
            ...mapState(['user']),
            currentChapterIndex() {
                let idx = -1;
                this.chapters.forEach((c, i) => {
                    if (this.current_ms >= c.start_time) idx = i;
                });
                return idx;
            },
            progressPct() {
                return this.duration_ms ? Math.min(100, this.current_ms / this.duration_ms * 100) : 0;
            },
            renderedMinutes() {
                return this.minutes ? this.renderLightMd(this.minutes.minute) : '';
            },
            // 摘要栏文案:联调实测 meeting_summary txt 是"带时间戳发言全文"(可达数十KB),
            // 真正的摘要在 smart/minutes 的 minute 开头"会议摘要:"段;按优先级提取
            summaryText() {
                const minute = (this.minutes && this.minutes.minute) || '';
                const idx = minute.indexOf('会议摘要');
                if (idx !== -1) {
                    const after = minute.slice(idx).replace(/^会议摘要[:\uFF1A]?\s*/, '');
                    const cut = after.search(/\n-{5,}|\n\n/);
                    const seg = (cut > 0 ? after.slice(0, cut) : after).trim().replace(/^[:\uFF1A\s]+/, '');
                    if (seg) return seg.slice(0, 600);
                }
                return ''; // meeting_summary txt 实为带时间戳发言全文,不作摘要兜底
            }
        },
        watch: {
            // 播放进入新章节时,章节缩略图条水平滚动到当前章节居中(只动水平,不打扰纵向浏览)
            currentChapterIndex(idx) {
                this.$nextTick(() => {
                    const strip = this.$refs.chapters;
                    if (!strip || idx < 0) return;
                    const el = strip.children[idx];
                    if (!el) return;
                    strip.scrollTo({
                        left: el.offsetLeft - strip.clientWidth / 2 + el.clientWidth / 2,
                        behavior: 'smooth'
                    });
                });
            }
        },
        created() {
            this.load();
        },
        methods: {
            submitPassword() {
                if (!this.share_password) { this.password_error = '请输入密码'; return; }
                this.password_error = '';
                this.load();
            },
            // 同一会话内刷新页面/刷新播放地址不必重复输密码;关掉浏览器即失效
            readSavedPassword(uuid) {
                try { return sessionStorage.getItem('rp_pwd_' + uuid) || ''; } catch (e) { return ''; }
            },
            saveSharePassword(uuid, pwd) {
                try {
                    if (pwd) sessionStorage.setItem('rp_pwd_' + uuid, pwd);
                    else sessionStorage.removeItem('rp_pwd_' + uuid);
                } catch (e) { /* 隐私模式等不可用时忽略 */ }
            },
            load(refresh) {
                this.loading = !refresh;
                this.video_error = false;
                // 本组件服务两个入口:
                //   /record-playback/:id  站内入口,走 $api(带登录态),后端做归属校验
                //   /playback/:token      开放接口签发的临时链接,凭 1 小时令牌免登录
                // 两个后端接口返回同一套响应信封({success,text,data}),所以下面的解析逻辑通用。
                const token = this.$route.params.token;
                const uuid = this.$route.params.uuid;
                // 三个入口:站内 record_id(需登录) / 开放接口临时 token / 分享链接 uuid(可带密码)
                let request;
                if (token) {
                    request = axios.get(this.$apiUrl('Open.Link') + '/' + encodeURIComponent(token),
                        {headers: {'Accept': 'application/json'}});
                } else if (uuid) {
                    if (!this.share_password) this.share_password = this.readSavedPassword(uuid);
                    request = axios.post(this.$apiUrl('Playback.Shared'),
                        {uuid, password: this.share_password}, {headers: {'Accept': 'application/json'}});
                } else {
                    request = this.$api('Playback.Data', {record_id: this.$route.params.id}, false);
                }
                request
                    .then(res => {
                        this.loading = false;
                        if (!res.data.success && res.data.data && res.data.data.need_password) {
                            // 首次打开不提示错误;带了密码仍失败才显示"密码错误"
                            this.password_error = this.share_password ? (res.data.text || '密码错误') : '';
                            this.need_password = true;
                            this.saveSharePassword(uuid, '');
                            return;
                        }
                        if (uuid && this.share_password) this.saveSharePassword(uuid, this.share_password);
                        this.need_password = false;
                        if (!res.data.success) {
                            if (refresh) { this.$message.error(res.data.text || '刷新失败,请稍后重试'); return; }
                            this.error = res.data.text || '加载失败';
                            return;
                        }
                        const d = res.data.data;
                        this.record = d.record || {};
                        this.video_url = d.video_url || '';
                        this.summary = (d.summary || '').trim();
                        this.keywords = d.keywords || [];
                        this.chapters = d.chapters || [];
                        this.transcript = d.transcript;
                        this.minutes = d.minutes;
                        this.topics = d.topics || [];
                        // 腾讯 mp4 的 moov 常在文件尾,metadata 可能很久才到:
                        // 先用章节/转写末尾时间估算时长,轨道条立即可用;loadedmetadata 后校准
                        if (!this.duration_ms) {
                            let est = 0;
                            this.chapters.forEach(c => { if (c.start_time > est) est = c.start_time; });
                            const paras = (this.transcript && this.transcript.paragraphs) || [];
                            if (paras.length) {
                                const last = paras[paras.length - 1];
                                est = Math.max(est, last.end_time || last.start_time || 0);
                            }
                            if (est > 0) this.duration_ms = est;
                        }
                        document.title = (this.record.topic || '录像回放') + ' - AI 回放';
                    })
                    .catch(() => {
                        this.loading = false;
                        if (refresh) { this.$message.error('刷新失败,请稍后重试'); return; }
                        this.error = '加载失败,请稍后重试';
                    });
            },
            handleBack() {
                window.history.length > 1 ? this.$router.back() : window.close();
            },
            // 左右分栏拖拽:向左拖增大右栏
            startResize(e) {
                const startX = e.clientX;
                const startW = this.right_width;
                const move = ev => {
                    const max = Math.max(360, Math.floor(window.innerWidth * 0.7));
                    this.right_width = Math.min(Math.max(startW + (startX - ev.clientX), 320), max);
                };
                const up = () => {
                    document.removeEventListener('mousemove', move);
                    document.removeEventListener('mouseup', up);
                    document.body.style.userSelect = '';
                    document.body.style.cursor = '';
                };
                document.addEventListener('mousemove', move);
                document.addEventListener('mouseup', up);
                document.body.style.userSelect = 'none'; // 拖拽期间禁止选中文本
                document.body.style.cursor = 'col-resize';
            },
            seekTo(ms) {
                const v = this.$refs.video;
                if (!v) return;
                v.currentTime = ms / 1000;
                const p = v.play();
                if (p && p.catch) p.catch(() => {});
            },
            handleMeta() {
                const v = this.$refs.video;
                if (v && v.duration && isFinite(v.duration)) this.duration_ms = Math.floor(v.duration * 1000);
            },
            dotPct(c) {
                return this.duration_ms ? Math.min(100, c.start_time / this.duration_ms * 100) : 0;
            },
            handleTrackClick(e) {
                if (!this.duration_ms) return;
                const rect = this.$refs.track.getBoundingClientRect();
                const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
                this.seekTo(Math.floor(ratio * this.duration_ms));
            },
            handleTimeUpdate() {
                const v = this.$refs.video;
                if (!v) return;
                // 秒级精度足够句级高亮,把 ~4Hz 的重渲染降到每秒最多一次
                const ms = Math.floor(v.currentTime) * 1000;
                if (ms !== this.current_ms) this.current_ms = ms;
            },
            formatMs(ms) {
                const total = Math.floor((parseInt(ms, 10) || 0) / 1000);
                const h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), s = total % 60;
                return (h ? h + ':' + (m < 10 ? '0' : '') : '') + m + ':' + (s < 10 ? '0' : '') + s;
            },
            formatCode(code) {
                return String(code || '').replace(/\s/g, '').replace(/(\d{3})(?=\d)/g, '$1 ');
            },
            // 轻量结构化渲染(不引依赖):适配腾讯纪要文本结构
            // 一、二级中文序号标题 / 1. 数字小标题 / - 键:值列表 / 字段行 / 分隔线
            renderLightMd(text) {
                const esc = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                const lines = (text || '').split('\n');
                let html = '', inList = false;
                const closeList = () => { if (inList) { html += '</ul>'; inList = false; } };
                lines.forEach(line => {
                    const t = line.trim();
                    if (t === '') return;
                    if (/^-{5,}$/.test(t)) { closeList(); html += '<hr class="md-hr"/>'; return; }
                    const li = t.match(/^[-·*]\s+(.*)/);
                    if (li) {
                        if (!inList) { html += '<ul class="md-ul">'; inList = true; }
                        const kv = li[1].match(/^([^:\uFF1A]{1,24})([:\uFF1A])([\s\S]*)$/);
                        html += '<li>' + (kv
                            ? '<strong>' + esc(kv[1]) + esc(kv[2]) + '</strong>' + esc(kv[3])
                            : esc(li[1])) + '</li>';
                        return;
                    }
                    closeList();
                    const h = t.match(/^(#{1,4})\s+(.*)/);
                    if (h) { html += '<h' + (h[1].length + 2) + '>' + esc(h[2]) + '</h' + (h[1].length + 2) + '>'; return; }
                    if (/^[一二三四五六七八九十]+、/.test(t)) { html += '<div class="md-h1">' + esc(t) + '</div>'; return; }
                    if (/^\d+[.、]\s*\S/.test(t)) { html += '<div class="md-h2">' + esc(t) + '</div>'; return; }
                    const field = t.match(/^(会议主题|发言人|会议摘要|会议待办)([:\uFF1A])([\s\S]*)$/);
                    if (field) {
                        html += '<p class="md-field"><strong>' + esc(field[1]) + esc(field[2]) + '</strong>' + esc(field[3]) + '</p>';
                        return;
                    }
                    html += '<p>' + esc(t) + '</p>';
                });
                closeList();
                return html;
            }
        }
    };
</script>

<style lang="scss">
.playback-page {
    height: 100vh;
    display: flex;
    flex-direction: column;
    background: #f5f6f8;

    .playback-header {
        padding: 8px 16px;
        background: #fff;
        border-bottom: 1px solid #e4e7ed;
        display: flex;
        align-items: center;

        .ph-brand {
            display: flex; align-items: center; margin-right: 16px;
            svg { display: block; }
            span { font-size: 15px; font-weight: 600; color: #2D8CFF; margin-left: 6px; }
        }
        .playback-title { font-size: 16px; font-weight: 600; margin: 0 12px; }
        .playback-meta { color: #909399; font-size: 13px; margin-right: 12px; }
        .ph-user { margin-left: auto; color: #606266; font-size: 13px; }
    }

    .playback-error { padding: 40px; }
    .playback-password {
        display: flex; justify-content: center; padding: 80px 20px;
        .pp-card { width: 340px; background: #fff; border-radius: 8px; padding: 28px 28px 24px;
            box-shadow: 0 2px 16px rgba(0,0,0,.08); }
        .pp-title { font-size: 16px; font-weight: 600; margin-bottom: 16px; color: #303133; }
        .pp-error { color: #F56C6C; font-size: 13px; margin-top: 8px; }
    }

    .playback-body {
        flex: 1;
        display: flex;
        overflow: hidden;
        min-height: 0; // 关键:flex 子项默认 min-height:auto 会被内容撑开,导致内部滚动失效变整页滚动

        .playback-left {
            flex: 1;
            min-width: 0;
            padding: 16px;
            overflow-y: auto;

            .playback-video-wrap {
                position: relative;
                background: #000;
                border-radius: 6px;

                video { width: 100%; max-height: 62vh; display: block; }

                .playback-video-empty { color: #909399; padding: 80px 0; text-align: center; }

                .playback-video-expired {
                    position: absolute; left: 0; right: 0; bottom: 48px; text-align: center;
                    span { color: #fff; margin-right: 10px; }
                }
            }

            .playback-track {
                position: relative;
                height: 14px;
                margin-top: 10px;
                cursor: pointer;
                background: #e4e7ed;
                background-clip: content-box;
                padding: 5px 0; // 扩大点击热区
                border-radius: 4px;

                .track-progress {
                    position: absolute; left: 0; top: 5px; bottom: 5px;
                    background: #409EFF; border-radius: 2px 0 0 2px; pointer-events: none;
                }
                .track-dot {
                    position: absolute; top: 50%; transform: translate(-50%, -50%);
                    width: 10px; height: 10px; border-radius: 50%;
                    background: #fff; border: 2px solid #909399; box-sizing: border-box;
                    transition: border-color 0.15s, transform 0.15s;

                    &:hover { border-color: #409EFF; transform: translate(-50%, -50%) scale(1.35); }
                    &.active { border-color: #409EFF; background: #409EFF; }
                }
            }

            .playback-summary {
                background: #fff; border-radius: 6px; padding: 12px 16px; margin-top: 12px;
                font-size: 13px; color: #303133; line-height: 1.7;

                .summary-collapsed {
                    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
                }
            }

            .playback-chapters {
                display: flex; overflow-x: auto; gap: 12px; margin-top: 12px; padding-bottom: 6px;

                .chapter-item {
                    flex: 0 0 128px; cursor: pointer;

                    img { width: 128px; height: 72px; object-fit: cover; border-radius: 4px; background: #ddd; }
                    .chapter-time { font-size: 12px; color: #909399; margin-top: 2px; }
                    .chapter-name { font-size: 12px; color: #303133; line-height: 1.4; }

                    &.active .chapter-name { color: #409EFF; font-weight: 600; }
                    &:hover .chapter-name { color: #409EFF; }
                }
            }
        }

        .playback-divider {
            flex: 0 0 6px;
            cursor: col-resize;
            background: transparent;
            border-left: 1px solid #e4e7ed;
            transition: background 0.15s;

            &:hover { background: #d0e2ff; }
        }

        .playback-right {
            flex: 0 0 auto;
            width: 420px; // 初始宽度;拖拽后由内联样式接管
            min-width: 320px;
            max-width: 70vw;
            min-height: 0;
            background: #fff;
            display: flex;
            flex-direction: column;

            .el-tabs { flex: 1; display: flex; flex-direction: column; padding: 0 12px; min-height: 0; }
            .el-tabs__content { flex: 1; overflow: hidden; min-height: 0; }
            .el-tab-pane { height: 100%; }

            .pane-scroll { height: 100%; overflow-y: auto; padding: 4px 2px 16px; }
            .pane-empty { color: #909399; padding: 40px 0; text-align: center; }

            .minutes-html {
                font-size: 13px; line-height: 1.8; color: #303133;
                h3, h4, h5, h6 { margin: 12px 0 6px; }
                p { margin: 4px 0; }
                .md-field { margin: 2px 0; strong { color: #303133; } }
                .md-hr { border: none; border-top: 1px solid #ebeef5; margin: 12px 0; }
                .md-h1 {
                    font-size: 14px; font-weight: 600; color: #303133;
                    margin: 16px 0 8px; padding-left: 8px; border-left: 3px solid #409EFF;
                }
                .md-h2 { font-weight: 600; color: #445; margin: 10px 0 4px; }
                .md-ul {
                    padding-left: 18px; margin: 4px 0 8px;
                    li { margin-bottom: 5px; strong { color: #303133; } }
                }
            }

            .topic-item {
                margin-bottom: 14px; font-size: 13px;
                .topic-title { color: #303133; font-weight: 600; margin-bottom: 4px; }
                .topic-segs { .topic-seg { margin: 0 6px 4px 0; cursor: pointer; } }
            }
        }
    }
}
</style>
